/**
 * When a photo says it was taken (ADR-0059): its EXIF `DateTimeOriginal`, else
 * `DateTimeDigitized`, read from the JPEG before anything re-encodes it. Never the file's
 * modification time (a phone sets it when the photo is picked) and never EXIF `DateTime`, which
 * an editor rewrites. A PNG, a JPEG without EXIF — a screenshot, a photo a messenger forwarded —
 * and bytes that do not parse say nothing.
 *
 * EXIF keeps a wall-clock time. With `OffsetTime…` beside it (phones write it) the moment is
 * exact; without, it is read in the device's own zone, which is where a photo from this phone's
 * gallery was most likely taken.
 */

/** EXIF sits in the first segments of the file; a phone's is well under this. */
const HEAD_BYTES = 256 * 1024

const TAG_EXIF_IFD = 0x8769
const TAG_ORIGINAL = 0x9003
const TAG_DIGITIZED = 0x9004
const TAG_OFFSET_ORIGINAL = 0x9011
const TAG_OFFSET_DIGITIZED = 0x9012
const TYPE_ASCII = 2

/** The wall-clock time EXIF holds, and the offset it was at when the photo names one. */
export interface ExifTime {
  /** `YYYY:MM:DD HH:MM:SS`, as stored. */
  local: string
  /** `+02:00`; null when the photo names none. */
  offset: string | null
}

/** The TIFF block of the JPEG's EXIF segment; null when there is none. */
function exifBlock(view: DataView): DataView | null {
  if (view.byteLength < 4 || view.getUint16(0) !== 0xffd8) return null
  let at = 2
  while (at + 4 <= view.byteLength && view.getUint8(at) === 0xff) {
    const marker = view.getUint8(at + 1)
    const length = view.getUint16(at + 2)
    // The image data starts: no metadata segment follows.
    if (marker === 0xda || length < 2) return null
    const isExif =
      marker === 0xe1 &&
      at + 10 <= view.byteLength &&
      view.getUint32(at + 4) === 0x45786966 && // "Exif"
      view.getUint16(at + 8) === 0
    if (isExif) {
      const start = at + 10
      const end = Math.min(at + 2 + length, view.byteLength)
      return end > start ? new DataView(view.buffer, view.byteOffset + start, end - start) : null
    }
    at += 2 + length
  }
  return null
}

/** A time is 20 bytes and an offset 7; a file that declares more is not believed. */
const MAX_TEXT_BYTES = 32

/** The entries of one IFD: tag → where its 12-byte entry starts; what runs past the block is left out. */
function entries(tiff: DataView, at: number, little: boolean): Map<number, number> {
  const found = new Map<number, number>()
  if (at + 2 > tiff.byteLength) return found
  const count = tiff.getUint16(at, little)
  for (let index = 0; index < count; index += 1) {
    const entry = at + 2 + index * 12
    if (entry + 12 > tiff.byteLength) break
    const tag = tiff.getUint16(entry, little)
    // The first entry of a tag is the tag's: a file that repeats one is read once.
    if (!found.has(tag)) found.set(tag, entry)
  }
  return found
}

/** The text of an ASCII entry; null for another type and for one that runs past the block. */
function ascii(tiff: DataView, entry: number | undefined, little: boolean): string | null {
  if (entry === undefined || tiff.getUint16(entry + 2, little) !== TYPE_ASCII) return null
  const length = tiff.getUint32(entry + 4, little)
  if (length > MAX_TEXT_BYTES) return null
  // Four bytes or fewer sit in the entry itself; more, at the offset it names.
  const start = length <= 4 ? entry + 8 : tiff.getUint32(entry + 8, little)
  if (start + length > tiff.byteLength) return null
  let text = ''
  for (let i = 0; i < length; i += 1) {
    const code = tiff.getUint8(start + i)
    if (code === 0) break
    text += String.fromCharCode(code)
  }
  return text.trim() || null
}

/** The time the EXIF of a JPEG's first bytes names; null when it names none. */
export function exifTime(bytes: ArrayBuffer): ExifTime | null {
  const tiff = exifBlock(new DataView(bytes))
  if (!tiff || tiff.byteLength < 8) return null
  const order = tiff.getUint16(0)
  if (order !== 0x4949 && order !== 0x4d4d) return null
  const little = order === 0x4949
  if (tiff.getUint16(2, little) !== 42) return null
  const pointer = entries(tiff, tiff.getUint32(4, little), little).get(TAG_EXIF_IFD)
  if (pointer === undefined) return null
  const exif = entries(tiff, tiff.getUint32(pointer + 8, little), little)
  const text = (tag: number) => ascii(tiff, exif.get(tag), little)
  const original = text(TAG_ORIGINAL)
  if (original) return { local: original, offset: text(TAG_OFFSET_ORIGINAL) }
  const digitized = text(TAG_DIGITIZED)
  return digitized ? { local: digitized, offset: text(TAG_OFFSET_DIGITIZED) } : null
}

const LOCAL = /^(\d{4}):(\d{2}):(\d{2}) (\d{2}):(\d{2}):(\d{2})$/
const OFFSET = /^[+-]\d{2}:\d{2}$/

/** The moment an EXIF time names; null for one that is no date (a camera writes zeros unset). */
export function exifMoment(time: ExifTime): Date | null {
  const parts = LOCAL.exec(time.local)
  if (!parts) return null
  const [year, month, day, hour, minute, second] = parts.slice(1).map(Number)
  if (year < 1990 || month < 1 || month > 12 || day < 1 || day > 31) return null
  const moment =
    time.offset && OFFSET.test(time.offset)
      ? new Date(
          `${parts[1]}-${parts[2]}-${parts[3]}T${parts[4]}:${parts[5]}:${parts[6]}${time.offset}`
        )
      : new Date(year, month - 1, day, hour, minute, second)
  return Number.isNaN(moment.getTime()) ? null : moment
}

/** When the photo says it was taken; null when it says nothing or cannot be read. */
export async function readPhotoTime(file: Blob): Promise<Date | null> {
  // Only a JPEG is read: nothing else carries the EXIF this looks for.
  if (file.type && file.type !== 'image/jpeg') return null
  try {
    const time = exifTime(await file.slice(0, HEAD_BYTES).arrayBuffer())
    return time && exifMoment(time)
  } catch {
    return null
  }
}
